import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Edit, MoreHorizontal, Calendar, Wrench, FileText, ChevronDown, ChevronUp, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { getPriorityBadgeClasses } from '../../utils/badges';
import { buildCorrigoWorkOrderUrl } from '../../utils/corrigo';
import { sanitizeHtml } from '../../utils/sanitize';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

const htmlToPlainText = (html: string): string => {
  if (!html) return '';
  const container = document.createElement('div');
  container.innerHTML = html;

  container.querySelectorAll('br').forEach((el) => {
    el.replaceWith('\n');
  });
  container.querySelectorAll('p, div, li, h1, h2, h3, h4, h5, h6, tr, blockquote, pre').forEach((el) => {
    el.appendChild(document.createTextNode('\n'));
  });

  const text = container.textContent || container.innerText || '';
  return text
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
};

interface WorkOrder {
  id: number;
  workOrderNumber: string;
  workOrderNumberFormatted?: string;
  clientName: string;
  createdDate?: string;
  dueDate?: string;
  externalWorkOrderNumber?: string;
  statusId: number;
  priorityColorCode?: string;
  priorityName?: string;
  invoiced?: boolean;
  equipmentId?: number;
  equipmentName?: string;
  description?: string;
}

interface Status {
  id: number;
  name: string;
}

interface JobContactSheetHeaderProps {
  workOrder: WorkOrder;
  statuses: Status[];
  fromPage: string;
  onStatusUpdate: (statusId: string) => void;
  onPrint: () => void;
  onCreateDepositInvoice: () => void;
  onCreateProgressInvoice: () => void;
  onCreateFinalInvoice: () => void;
  onCreateChangeOrderInvoice: () => void;
  onEmailClient: () => void;
  onExportPDF: () => void;
  onDeleteWorkOrder?: () => void;
}

const JobContactSheetHeader: React.FC<JobContactSheetHeaderProps> = ({
  workOrder,
  statuses,
  fromPage,
  onStatusUpdate,
  onPrint,
  onCreateDepositInvoice,
  onCreateProgressInvoice,
  onCreateFinalInvoice,
  onCreateChangeOrderInvoice,
  onEmailClient,
  onExportPDF,
  onDeleteWorkOrder,
}) => {
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const plainDescription = useMemo(
    () => htmlToPlainText(workOrder.description || ''),
    [workOrder.description]
  );

  const handleToggleDescription = () => {
    setIsDescriptionExpanded((prev) => !prev);
  };

  const handleDescriptionKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleToggleDescription();
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-40 shadow-sm">
      <div className="px-6 py-4">
        {/* Top Row: Back + WO# + Client + Due Date + External WO# */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-3">
            <Link 
              to={fromPage === 'calendar' ? '/workorders' : `/${fromPage}`} 
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <div className="flex items-center space-x-3">
                <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {workOrder.workOrderNumberFormatted || workOrder.workOrderNumber}
                </h1>
                <span className="text-gray-500 dark:text-gray-400">:</span>
                <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">
                  {workOrder.clientName}
                </span>
              </div>
              <div className="flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400 mt-1">
                {workOrder.createdDate && (
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">Created:</span>
                    <span>{new Date(workOrder.createdDate).toLocaleDateString()}</span>
                  </div>
                )}
                {workOrder.dueDate && (
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span className="font-medium">Due:</span>
                    <span>{new Date(workOrder.dueDate).toLocaleDateString()}</span>
                  </div>
                )}
                {workOrder.externalWorkOrderNumber && (
                  <div className="flex items-center space-x-1">
                    <span className="font-medium">Ext:</span>
                    <a
                      href={buildCorrigoWorkOrderUrl(workOrder.externalWorkOrderNumber)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 font-mono text-blue-600 dark:text-blue-400 hover:underline"
                      title="Open in Corrigo"
                      aria-label={`Open Corrigo work order ${workOrder.externalWorkOrderNumber}`}
                    >
                      <span>{workOrder.externalWorkOrderNumber}</span>
                      <ExternalLink className="h-3 w-3" aria-hidden="true" />
                    </a>
                  </div>
                )}
                {workOrder.equipmentId && workOrder.equipmentName && (
                  <Link
                    to={`/equipment/${workOrder.equipmentId}`}
                    className="flex items-center space-x-1 text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <Wrench className="h-3 w-3" />
                    <span className="font-medium">Equipment:</span>
                    <span>{workOrder.equipmentName}</span>
                  </Link>
                )}
              </div>
            </div>
          </div>
          
          {/* Priority Badge */}
          <div className="flex items-center space-x-3">
            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadgeClasses({ colorCode: workOrder.priorityColorCode })}`}>
              Priority: {workOrder.priorityName}
            </span>
          </div>
        </div>

        {/* Second Row: Status + Edit + More Menu */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Status:</span>
            <Select value={workOrder.statusId.toString()} onValueChange={onStatusUpdate}>
              <SelectTrigger className="w-auto min-w-[200px] bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-600">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
                {statuses.map((status) => (
                  <SelectItem 
                    key={status.id} 
                    value={status.id.toString()} 
                    className="text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 pl-3 pr-2 [&>span:first-child]:hidden"
                  >
                    {status.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="flex items-center space-x-2">
            <Link 
              to={`/workorders/${workOrder.id}/edit?from=jcs`} 
              className="flex items-center space-x-2 px-3 py-1.5 bg-accent-color text-white rounded-md hover:bg-accent-color-600 transition-colors"
            >
              <Edit className="h-4 w-4" />
              <span>Edit</span>
            </Link>
            
            {/* More Actions Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="px-3 py-1.5">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onPrint}>
                  Print
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onEmailClient}>
                  Email Client
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onExportPDF}>
                  Export PDF
                </DropdownMenuItem>
                {onDeleteWorkOrder && (
                  <>
                    <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                    <DropdownMenuItem onClick={onDeleteWorkOrder} className="text-red-600 dark:text-red-400">
                      Delete
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {plainDescription && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={handleToggleDescription}
              onKeyDown={handleDescriptionKeyDown}
              aria-expanded={isDescriptionExpanded}
              aria-controls="job-contact-description"
              aria-label={isDescriptionExpanded ? 'Collapse work order description' : 'Expand work order description'}
              tabIndex={0}
              className="flex w-full items-start gap-2 text-left rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/50 px-1 py-1 -mx-1 transition-colors"
            >
              <FileText className="h-4 w-4 shrink-0 mt-0.5 text-gray-500 dark:text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 shrink-0">
                Description
              </span>
              {!isDescriptionExpanded && (
                <span className="flex-1 min-w-0 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line line-clamp-2">
                  {plainDescription}
                </span>
              )}
              <span className="ml-auto shrink-0 mt-0.5 text-gray-400 dark:text-gray-500">
                {isDescriptionExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </span>
            </button>
            {isDescriptionExpanded && (
              <div
                id="job-contact-description"
                className="mt-2 max-h-56 overflow-y-auto rounded-md bg-gray-50 dark:bg-gray-900/60 px-3 py-2"
              >
                <div
                  className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap break-words [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:mb-2 [&_ol]:mb-2 [&_li]:mb-1 [&_br]:block [&_img]:max-w-full [&_img]:h-auto"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(workOrder.description) }}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default JobContactSheetHeader;
